import React from 'react';
import { Loader } from '../../../components/Loader';
import { FiEye } from 'react-icons/fi';

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '-';
  const units = ['B','KB','MB','GB','TB'];
  let size = Math.max(bytes, 0);
  let idx = 0;
  while (size >= 1024 && idx < units.length - 1) {
    size /= 1024;
    idx += 1;
  }
  return `${size.toFixed(size < 10 && idx > 0 ? 1 : 0)} ${units[idx]}`;
};

export function ContactTransactionsCard({
  invoices,
  invLoading,
  invError,
  showNewInv,
  setShowNewInv,
  invSaving,
  invSaveErr,
  setInvSaveErr,
  invForm,
  setInvForm,
  balanceBDT,
  onSaveTransaction,
  pendingAttachments = [],
  onOpenAttachmentModal = () => {},
  onRemoveAttachment,
  onClearAttachments,
  onViewInvoicePhoto,
}) {
  return (
    <div className="card h-100">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h5 className="mb-0">Transactions</h5>
        <div className="d-flex align-items-center gap-2">
          {invLoading ? <Loader size="sm" /> : null}
          <span className="badge bg-light text-dark">Balance: BDT {Number(balanceBDT || 0).toLocaleString()}</span>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => {
              setShowNewInv(v => {
                const next = !v;
                if (v && typeof onClearAttachments === 'function') onClearAttachments();
                return next;
              });
              setInvSaveErr('');
            }}
          >
            {showNewInv ? 'Close' : 'New Transaction'}
          </button>
        </div>
      </div>
      <div className="card-body">
        {showNewInv && (
          <div className="border rounded p-2 mb-3">
            <div className="row g-2 align-items-end">
              <div className="col-sm-2">
                <label className="form-label mb-1">Type</label>
                <select className="form-select" value={invForm.type}
                  onChange={(e)=>setInvForm(f=>({...f, type: e.target.value}))}>
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </div>
              <div className="col-sm-2">
                <label className="form-label mb-1">Amount</label>
                <input className="form-control" type="number" min="0" step="0.01"
                  value={invForm.amount}
                  onChange={(e)=>setInvForm(f=>({...f, amount: e.target.value}))}
                  placeholder="0.00" />
              </div>
              
              <div className="col-sm-3">
                <label className="form-label mb-1">Date</label>
                <input className="form-control" type="date" value={invForm.date}
                  onChange={(e)=>setInvForm(f=>({...f, date: e.target.value}))} />
              </div>
              <div className="col-sm-12">
                <label className="form-label mb-1">Description</label>
                <textarea className="form-control" rows={2}
                  value={invForm.description}
                  onChange={(e)=>setInvForm(f=>({...f, description: e.target.value}))}
                  placeholder="Optional details" />
              </div>
              <div className="col-sm-12">
                <div className="d-flex justify-content-between align-items-center">
                  <label className="form-label mb-1">Invoice Photos</label>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={onOpenAttachmentModal}>
                    Select Photos
                  </button>
                </div>
                {pendingAttachments.length === 0 ? (
                  <div className="text-muted small">No photos selected.</div>
                ) : (
                  <ul className="list-group list-group-flush small border rounded">
                    {pendingAttachments.map((file, idx) => (
                      <li
                        key={`${file.name}-${file.size}-${file.lastModified}-${idx}`}
                        className="list-group-item d-flex align-items-center justify-content-between py-2"
                      >
                        <div className="me-2 text-truncate">
                          <div className="fw-medium text-truncate" title={file.name}>{file.name}</div>
                          <div className="text-muted">{formatSize(file.size)}</div>
                        </div>
                        {onRemoveAttachment && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={()=> onRemoveAttachment(idx)}
                          >
                            Remove
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="form-text">Selected photos upload after saving the transaction.</div>
              </div>
              {invSaveErr && (
                <div className="col-sm-12 text-danger small">{invSaveErr}</div>
              )}
              <div className="col-sm-12 d-flex justify-content-end gap-2">
                <button className="btn btn-outline-secondary" onClick={()=>{ setShowNewInv(false); setInvSaveErr(''); if (onClearAttachments) onClearAttachments(); }}>Cancel</button>
                <button className="btn btn-success" disabled={invSaving || !invForm.type || !(parseFloat(invForm.amount) > 0)}
                  onClick={onSaveTransaction}
                >
                  {invSaving ? 'Saving...' : 'Save Transaction'}
                </button>
              </div>
            </div>
          </div>
        )}
        {invError && <div className="text-danger mb-2">{invError}</div>}
        {(!invoices || invoices.length === 0) ? (
          <div className="text-muted">No transactions for this contact.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th style={{width:'20%'}}>Date</th>
                  <th style={{width:'10%'}}>Type</th>
                  <th style={{width:'15%'}} className="text-end">Amount</th>
                  <th>Description</th>
                  <th style={{width:'10%'}} className="text-center">Attachments</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv._id}>
                    <td>{new Date(inv.date || inv.createdAt).toLocaleString?.() || inv.date || inv.createdAt}</td>
                    <td>
                      <span className={inv.type === 'income' ? 'badge bg-success' : 'badge bg-danger'}>
                        {inv.type}
                      </span>
                    </td>
                    <td className="text-end">{inv.type === 'expense' ? '-' : ''}{Number(inv.amount || 0).toLocaleString()}</td>
                    <td style={{whiteSpace:'pre-wrap'}}>{inv.description || ''}</td>
                    <td className="text-center">
                      {Array.isArray(inv.photoFiles) && inv.photoFiles.length > 0 ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={()=> onViewInvoicePhoto?.(inv.photoFiles)}
                        >
                          <FiEye /> View ({inv.photoFiles.length})
                        </button>
                      ) : (
                        <span className="text-muted small">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
